import React from 'react';
import { CompleteKundali, PlanetName, ChartStyle, Language } from '../types/kundali';
import { UI_TEXT, getPlanetFullName, getDignityName } from '../utils/language';
import { Award, ArrowUp, ArrowDown } from 'lucide-react';
import { NorthIndianSvgChart, SouthIndianSvgChart, EastIndianSvgChart } from './ChartViewer';

interface PlanetStrengthDashboardProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const PlanetStrengthDashboard: React.FC<PlanetStrengthDashboardProps> = ({ kundali, lang = 'en' }) => {
  const [selectedDivisionalCode, setSelectedDivisionalCode] = React.useState<string>('D1');
  const [chartStyle, setChartStyle] = React.useState<ChartStyle>('north');
  const ui = UI_TEXT[lang as 'en' | 'hi'] || UI_TEXT['en'];

  const divisionalList = [
    { code: 'D1', label: 'D1' },
    { code: 'D9', label: 'D9' }
  ];

  const currentChartData = (kundali.charts as any)[selectedDivisionalCode] || kundali.charts.D1;

  // Natural Beneficence Mapping
  const getNaturalBeneficence = (planet: PlanetName): 'benefic' | 'malefic' => {
    const naturalMap: Record<string, 'benefic' | 'malefic'> = {
      'Sun': 'malefic',
      'Moon': 'benefic',
      'Mars': 'malefic',
      'Mercury': 'benefic',
      'Jupiter': 'benefic',
      'Venus': 'benefic',
      'Saturn': 'malefic',
      'Rahu': 'malefic',
      'Ketu': 'malefic'
    };
    return naturalMap[planet] || 'benefic';
  };

  // Functional Beneficence logic
  const getFunctionalBeneficence = (lagnaSign: string, planet: PlanetName): 'benefic' | 'malefic' | 'neutral' => {
    return 'neutral'; 
  };

  const ChartComponent = chartStyle === 'north' ? NorthIndianSvgChart : chartStyle === 'south' ? SouthIndianSvgChart : EastIndianSvgChart;

  return (
    <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Chart Area */}
      <div className="flex flex-col items-center">
        <div className="flex items-center justify-between w-full mb-4">
          <h3 className="font-bold text-astro-primary font-serif">{currentChartData.title}</h3>
          <div className="flex gap-2">
            {['north', 'south', 'east'].map((style) => (
              <button
                key={style}
                onClick={() => setChartStyle(style as ChartStyle)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                  chartStyle === style
                    ? 'bg-astro-primary text-white shadow-sm'
                    : 'bg-astro-card text-astro-secondary-text border border-astro-border hover:text-astro-text'
                }`}
              >
                {style === 'north' ? ui.northIndian : style === 'south' ? ui.southIndian : ui.eastIndian}
              </button>
            ))}
          </div>
        </div>
        <div className="w-full max-w-[400px] aspect-square relative">
          <ChartComponent
            chartData={currentChartData}
            planets={currentChartData.planets}
            ashtakvarga={kundali.ashtakvarga}
            onSelectPlanet={() => {}}
            lang={lang}
            showDegrees={false}
          />
        </div>
      </div>

      {/* Dignity Table Area */}
      <div className="space-y-4">
        <div className="border-b border-astro-border pb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-astro-primary font-serif flex items-center gap-2">
            <Award className="w-6 h-6 text-astro-primary" />
            {lang === 'hi' ? 'ग्रह बल और शुभता' : 'Planet Strength & Beneficence'} ({selectedDivisionalCode})
          </h2>
          <div className="flex gap-2">
            {divisionalList.map(d => (
              <button
                key={d.code}
                onClick={() => setSelectedDivisionalCode(d.code)} 
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                  selectedDivisionalCode === d.code
                    ? 'bg-astro-primary text-white shadow-sm'
                    : 'bg-astro-card text-astro-secondary-text border border-astro-border hover:text-astro-text'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentChartData.planets.filter((p: any) => p.name !== 'Ascendant').map((planet: any, idx: number) => (
            <div key={idx} className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-1.5">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-bold text-astro-text text-sm">{getPlanetFullName(planet.name, lang)}</span>
                  <span className="text-[11px] text-astro-secondary-text block">
                    {planet.sign} {planet.dignity === 'Exalted' ? (lang === 'hi' ? '(उच्च राशि)' : '(Exalted Sign)') : planet.dignity === 'Debilitated' ? (lang === 'hi' ? '(नीच राशि)' : '(Debilitated Sign)') : ''}
                  </span>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 shadow-xs border ${
                  planet.dignity === 'Exalted' ? 'bg-emerald-100 text-emerald-900 border-emerald-300' :
                  planet.dignity === 'Debilitated' ? 'bg-rose-100 text-rose-900 border-rose-300' :
                  planet.dignity === 'Own House' ? 'bg-teal-50 text-astro-primary border-teal-300' :
                  'bg-astro-bg text-astro-secondary-text border-astro-border'
                }`}>
                  {planet.dignity === 'Exalted' && <ArrowUp className="w-3.5 h-3.5 text-emerald-700" />}
                  {planet.dignity === 'Debilitated' && <ArrowDown className="w-3.5 h-3.5 text-rose-700" />}
                  {getDignityName(planet.dignity, lang)}
                </span>
              </div>
              <div className="text-xs text-astro-secondary-text pt-1 border-t border-astro-border/60">
                <div>{lang === 'hi' ? 'प्राकृतिक' : 'Natural'}: {ui[getNaturalBeneficence(planet.name as PlanetName)]}</div>
                <div>{lang === 'hi' ? 'कार्यात्मक' : 'Functional'}: {ui[getFunctionalBeneficence(kundali.houses[0].sign, planet.name as PlanetName)]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
